/**
 * The Categories part of the filters window: one toggle per category the graph's nodes use
 * (none at first: people make them by naming them on a node), plus "No category". A right
 * click on one renames it, shows only it, or takes every node out of it (one undo each).
 */
import { For, Show, createMemo, createSignal, type JSX } from "solid-js";
import { useApp } from "../../app/context";
import { categoryCounts, categoryVar, NO_CATEGORY_VAR, removeEdits, renameEdits, sameCategory } from "../../shared/categories";
import { SEPARATOR } from "../../state/menu";
import { toast } from "../../shared/toast";

/** The categories section. */
export function CategoryFilters(): JSX.Element {
  const { view, graph, actions, menu } = useApp();
  const counts = createMemo(() => categoryCounts(graph.state.graph));
  const without = createMemo(() => graph.state.graph.nodes.filter((n) => n.categories.length === 0).length);
  /** The category being renamed (its toggle turns into a text field). */
  const [renaming, setRenaming] = createSignal<string | null>(null);
  const shown = (name: string) => !view.state.hiddenCategories.includes(name.toLowerCase());

  /** Renames a category on every node (one undo); merging into an existing one is fine. */
  const rename = (from: string, to: string) => {
    // Enter, then the field's blur as it goes: only the first counts.
    if (renaming() !== from) return;
    setRenaming(null);
    const name = to.trim();
    if (!name || name === from) return;
    const wasShown = shown(from);
    void actions.batch(renameEdits(graph.state.graph, from, name)).then((changes) => {
      if (!changes) return;
      if (wasShown) view.showCategory(name);
      toast(`Renamed ${from} to ${name}. Press Ctrl+Z to undo.`);
    });
  };
  /** Shows only this category (and hides nodes in none). */
  const only = (name: string) => {
    for (const c of ["", ...counts().map((c) => c.name)]) {
      const hide = !sameCategory(c, name) || c === "";
      if (hide === shown(c)) view.toggleCategory(c);
    }
  };
  /** Takes every node out of a category (the nodes stay). */
  const remove = (name: string) =>
    void actions.batch(removeEdits(graph.state.graph, name)).then((changes) => changes && toast(`No node is in ${name} any more. Press Ctrl+Z to undo.`));

  return (
    <div>
      <h2>Categories</h2>
      <div class="stack" id="category-filters">
        <Show when={counts().length === 0}>
          <p class="muted small" id="no-categories">
            No categories yet. Name one on a node (inspector or Add node) to sort and colour nodes by it.
          </p>
        </Show>
        <For each={counts()}>
          {(c) => (
            <Show
              when={renaming() !== c.name}
              fallback={
                <div class="chip">
                  <input
                    class="field rename"
                    value={c.name}
                    aria-label={`New name for ${c.name}`}
                    data-category-rename={c.name}
                    ref={(el) => queueMicrotask(() => (el.focus(), el.select()))}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") setRenaming(null);
                      if (e.key === "Enter") rename(c.name, e.currentTarget.value);
                    }}
                    onBlur={(e) => rename(c.name, e.currentTarget.value)}
                  />
                </div>
              }
            >
              <button
                type="button"
                class="chip"
                data-category={c.name}
                aria-pressed={shown(c.name)}
                title="Click to show or hide; right-click to rename"
                onClick={() => view.toggleCategory(c.name)}
                onDblClick={() => setRenaming(c.name)}
                onContextMenu={(e) =>
                  menu.open(e, [
                    { heading: `Category · ${c.name}` },
                    { label: "Rename…", action: () => setRenaming(c.name) },
                    { label: "Show only this one", action: () => only(c.name) },
                    SEPARATOR,
                    { label: "Take every node out of it", danger: true, action: () => remove(c.name) },
                  ])
                }
              >
                <i class="dot" style={{ background: `var(${categoryVar(graph.categories(), c.name)})` }} />
                {c.name}
                <span class="n">{c.count}</span>
              </button>
            </Show>
          )}
        </For>
        <Show when={counts().length > 0 || view.state.hiddenCategories.includes("")}>
          <button type="button" class="chip" data-category="" aria-pressed={shown("")} onClick={() => view.toggleCategory("")}>
            <i class="dot" style={{ background: `var(${NO_CATEGORY_VAR})` }} />
            No category
            <span class="n">{without()}</span>
          </button>
        </Show>
      </div>
    </div>
  );
}
